/**
 * Bookings that already exist.
 *
 * Seed data standing in for an orders API. Nothing here is created by the
 * cart - checkout has no backend to post to - so treat this as "what a
 * returning customer would see". When the API arrives, keep the shape and swap
 * where the array comes from.
 *
 * Dates are plain strings rather than `Date`s on purpose: these are display
 * lines, and parsing them back only to format them again would invent a
 * precision the data does not have. Real bookings should carry an ISO
 * timestamp and format at the edge.
 */

import { BookingMode } from './booking';
import { CategoryKey } from './services';

export type BookingStatus = 'live' | 'completed' | 'cancelled' | 'recurring';

/**
 * How far along a booking is, independent of its status.
 *
 * `status` is the shelf it lives on in the list; `stage` is what the detail
 * screen shows. They are not the same axis - a booking is "live" through four
 * of these five, and which one it is decides whether the screen shows an ETA,
 * an OTP, or a rating form.
 */
export type BookingStage =
  | 'booked'
  | 'assigned'
  | 'onway'
  | 'service'
  | 'completed';

/** The stepper, in order. Every detail screen draws all five. */
export const bookingStages: Array<{ key: BookingStage; label: string }> = [
  { key: 'booked', label: 'Booked' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'onway', label: 'On the way' },
  { key: 'service', label: 'Service' },
  { key: 'completed', label: 'Completed' },
];

export const stageIndex = (stage: BookingStage): number =>
  bookingStages.findIndex(item => item.key === stage);

export type Professional = {
  name: string;
  /** Out of 5, shown to one decimal. */
  rating: number;
  /** How many jobs they have completed. */
  jobs: number;
  /** What they are certified in - "Deep Cleaning Expert". */
  title: string;
  /** Background-checked; drives the tick beside the name. */
  verified: boolean;
  /**
   * Their photo, as a signed URL from the platform.
   *
   * Undefined on the bundled examples and on any professional who has not
   * uploaded one - the card falls back to their initials, which is a real
   * design rather than a placeholder. Never persisted: the URL is short-lived
   * and is re-read from the booking on every load.
   */
  photoUrl?: string;
};

export type Booking = {
  id: string;
  /** The basket this booking was placed in, when it came from one. Pays as one order. */
  checkoutId?: string;
  /** The service booked. Matches a `Service.name` where one exists. */
  title: string;
  /**
   * The catalogue id of what was booked.
   *
   * Set on anything that came from the platform; absent on the bundled
   * examples, which predate the catalogue having ids at all.
   *
   * This is what "Book again" resolves through. Matching by `title` was the
   * only option before and it is a guess - the app's catalogue calls it
   * "Bathroom deep cleaning" and the platform's calls it "Bathroom Deep
   * Clean", so a rebook silently found nothing and did nothing.
   */
  serviceId?: string;
  /**
   * The saved address this was booked against, server-side.
   *
   * Set alongside `serviceId` - same rule, same reason: present on anything
   * from the platform, absent on the bundled examples. This is what lets a
   * reschedule recreate the booking at a new time without asking the
   * customer to pick the address again; see `useBookings.reschedule`.
   */
  addressId?: string;
  /**
   * How this was paid for - `'cash'` or `'online'`, frozen at creation.
   *
   * Same platform-only availability as `addressId`. A reschedule reuses it
   * rather than asking again, since the payment mode was already decided
   * once for this visit.
   */
  paymentMode?: 'cash' | 'online';
  /**
   * Placed, but not paid for - so nothing is happening on it yet.
   *
   * An online booking is created BEFORE the gateway opens, because the
   * server mints the Razorpay order from the booking’s own amount and has
   * nothing to mint it against until the booking exists. A customer who
   * backs out of the sheet therefore leaves a real booking behind.
   *
   * It is not live work. Dispatch never sees it, no professional is being
   * looked for, and the server releases it after
   * `booking.paymentHoldWindowMinutes`. Every screen that would otherwise
   * draw it as live has to check this first - saying "Finding a
   * professional" over an unpaid booking is the app inventing activity
   * that is not happening.
   */
  awaitingPayment?: boolean;
  category: CategoryKey;
  status: BookingStatus;
  /** When it happens, or happened - already formatted for display. */
  when: string;
  /**
   * When it actually starts, as an ISO instant.
   *
   * `when` is a display line and nothing can be computed from it - "Today,
   * 10:00 AM" does not say which today, and the seeded examples were written
   * by hand. This is the machine-readable twin, and it is what the free-
   * cancellation window is measured against.
   *
   * Absent on the bundled examples and on instant bookings, which have no
   * scheduled instant to be six hours before. Callers must treat `undefined`
   * as "we cannot tell" and fall back to the booking's stage rather than
   * assuming the visit is far away - see `data/cancellation.ts`.
   */
  slotStartAt?: string;
  /** When it ends, as an ISO instant. Moves when the job is extended. */
  slotEndAt?: string;
  /**
   * Hours of a Pro this bought, and minutes added once it was running.
   *
   * `hours` is null on a fixed-price service, which is the whole test for
   * whether this booking can be extended at all.
   */
  hours?: number | null;
  extensionMinutes?: number;
  /** Minutes this visit may still run, from the server. Zero means no. */
  extendableMinutes?: number;
  /** Where, in one line. */
  address: string;
  /** What was paid, in whole rupees. */
  price: number;
  /** Who is doing it. Absent on cancelled work nobody was assigned to. */
  professional?: Professional;

  // ------------------------------------------------------- the detail screen
  /** What the customer quotes on the phone. Not the internal `id`. */
  reference: string;
  stage: BookingStage;
  /** Every service in the booking, for the collapsible list. */
  services: string[];
  /** How long the visit is expected to take - "2–3 hrs". */
  duration: string;
  /** What has been paid, and how. */
  payment: { paid: number; method: string };

  /**
   * On the way: how long until they arrive.
   *
   * The only thing the app says about a professional's whereabouts. See the
   * note on the `onway` branch in `BookingDetailScreen`.
   */
  eta?: string;
  /**
   * How far away they are - "2.4 km away".
   *
   * NOT RENDERED ANYWHERE. Both places that drew it are commented out: the
   * tracking panel on the detail screen and the line under the card on the
   * bookings list. Kept on the type and in the seed data so restoring the
   * panel does not also mean re-authoring the data, but treat it as dead: a
   * distance is a position, and this app deliberately does not report one.
   */
  distance?: string;
  /** Arrived: the code the customer reads out to start the work. */
  otp?: string;
  /**
   * In progress: the code read out to FINISH the work.
   *
   * Minted when the job starts, shown while it runs, spent when the Pro
   * enters it to complete - the same consent handshake as `otp`, at the
   * other end of the visit. Absent on bookings from a backend that predates
   * it.
   */
  endOtp?: string;
  /** In progress: when work actually began. */
  startedAt?: string;
  /**
   * When the job finished, server-sourced.
   *
   * Only the Home bar reads it, to decide whether a finished job is recent
   * enough to still be worth asking about - a rating prompt for something done
   * last month is furniture, not a question.
   */
  completedAt?: string;
  /** Only on recurring: how often it repeats. */
  cadence?: string;
  /** Only on live: the one line that says what is happening right now. */
  progress?: string;
  /**
   * What the visit covers, in a word each.
   *
   * The title names the service; these say what is inside it, which is what
   * someone checks when two bookings have similar names. Listed rather than
   * derived: "Full home deep clean" covers a kitchen and two bathrooms, and no
   * amount of reading the title tells you that.
   */
  tags: string[];

  // -------------------------------------------------- money after the fact
  /**
   * What actually came back, once a cancellation has one. Set only on real,
   * platform-sourced bookings - see `serviceId` - since the seeded examples
   * have no server row to carry it. `undefined` is "nothing to show", not
   * "zero was refunded".
   */
  refundedAmount?: number;
  /**
   * When the cancellation that produced the refund happened. ISO date-time.
   *
   * Carried for the wallet's activity list, which merges refunds with coin
   * movements and has to sort the two together. `when` cannot do that job -
   * it is a display string like "Today, 10:00 AM" with no year in it, and
   * ordering a list by parsing that would guess. This is a real timestamp
   * from the server, or undefined on a booking that was never cancelled.
   */
  cancelledAt?: string;
  /** Deducted from the refund above. Present only where one was charged. */
  cancellationFeeAmount?: number;
  /**
   * Homingo Coins spent on this booking, if any were.
   *
   * Money and coins settle down two different pipes - the card refund goes
   * through the gateway over days, the coins go back to the ledger at once -
   * so they are carried as two figures and shown as two lines. Collapsing
   * them into one rupee total is what makes a customer wait a week for coins
   * that are already back.
   */
  coinsRedeemed?: number;
  /** Coins returned by a cancellation. Present only once one has happened. */
  coinsRefunded?: number;
  /** Set once the work is billed. What the invoice screen quotes. */
  invoiceNumber?: string;
  /**
   * The GST on that invoice - the part of what was paid that is tax, however
   * the booking was priced (inside the price, or added on top of it).
   */
  taxAmount?: number;
  /** Time added while the job ran, paid for on top of `price`. */
  extensionAmount?: number;
  /**
   * What was said about the finished job, if anything - server-sourced, same
   * scoping as the four fields above. The composer's photos are kept
   * separately in `HomeScreen`'s own `reviews` state, not here: nothing
   * uploads a photo anywhere yet, so `photoUrls` from the server is honest
   * but often empty, and the local copy is what actually remembers them for
   * the rest of the session.
   */
  review?: {
    stars: number;
    comment: string | null;
    tags: string[];
    photoUrls: string[];
  };
};

/** Tags shown before the rest collapse into a "+N" chip. */
export const TAG_LIMIT = 3;

export const bookings: Booking[] = [
  /*
   * One booking per stage, so every state of the detail screen is reachable
   * without a backend to advance one. `bk-1042` is the walkthrough: change its
   * `stage` to see the ETA, the OTP, the progress panel or the rating form.
   */
  {
    id: 'bk-1042',
    reference: '#HOM12846',
    title: 'Deep home cleaning',
    category: 'cleaning',
    status: 'live',
    stage: 'onway',
    when: 'Today, 10:00 AM',
    duration: '2–3 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 2799,
    payment: { paid: 200, method: 'UPI' },
    professional: {
      name: 'Rahul Singh',
      rating: 4.8,
      jobs: 312,
      title: 'Deep Cleaning Expert',
      verified: true,
    },
    progress: 'Professional is on the way',
    eta: '18 mins',
    distance: '2.4 km away',
    services: ['Full Home Deep Cleaning (2 BHK)'],
    tags: ['Cleaning', 'Kitchen', 'Bathroom', 'Balcony'],
  },
  {
    id: 'bk-1043',
    reference: '#HOM12851',
    title: 'Bathroom deep cleaning',
    category: 'cleaning',
    status: 'live',
    stage: 'assigned',
    when: 'Today, 4:00 PM',
    duration: '1–2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 519,
    payment: { paid: 100, method: 'UPI' },
    professional: {
      name: 'Anita Rao',
      rating: 4.9,
      jobs: 508,
      title: 'Bathroom Care Specialist',
      verified: true,
    },
    progress: 'Starts in about 5 hours',
    services: ['Bathroom Deep Clean'],
    tags: ['Cleaning', 'Bathroom'],
  },
  {
    id: 'bk-1044',
    reference: '#HOM12853',
    title: 'AC service & filter clean',
    category: 'appliance',
    status: 'live',
    /* Still "on the way" as far as the stepper is concerned - arriving is not
       a sixth step, it is the end of the third. The `otp` below is what tells
       the screen to show the code panel instead of the ETA. */
    stage: 'onway',
    when: 'Today, 11:00 AM',
    duration: '1 hr',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 1998,
    payment: { paid: 200, method: 'UPI' },
    professional: {
      name: 'Rakesh Patel',
      rating: 4.7,
      jobs: 221,
      title: 'Appliance Technician',
      verified: true,
    },
    progress: 'Professional has arrived',
    /* At the door, so there is no travel left to estimate - the estimate is
       now when the work finishes. */
    eta: 'Done by 12:00 PM',
    otp: '4821',
    services: ['Split AC Service'],
    tags: ['Appliance', 'AC'],
  },
  {
    id: 'bk-1045',
    reference: '#HOM12855',
    title: 'Full Home Deep Cleaning (2 BHK)',
    category: 'cleaning',
    status: 'live',
    stage: 'service',
    when: 'Today, 10:00 AM',
    duration: '2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 1299,
    payment: { paid: 200, method: 'UPI' },
    professional: {
      name: 'Imran Sheikh',
      rating: 4.6,
      jobs: 184,
      title: 'Cleaning Expert',
      verified: true,
    },
    progress: 'Work has started',
    eta: 'Done by 12:18 PM',
    startedAt: '10:18 AM',
    services: ['Full Home Deep Cleaning (2 BHK)'],
    tags: ['Cleaning', '2 BHK'],
  },
  {
    id: 'bk-1038',
    reference: '#HOM12790',
    title: 'Bathroom deep cleaning',
    category: 'cleaning',
    status: 'recurring',
    stage: 'booked',
    when: 'Next: Thu, 12 Jun',
    duration: '1–2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 519,
    payment: { paid: 519, method: 'UPI' },
    professional: {
      name: 'Anita Rao',
      rating: 4.9,
      jobs: 508,
      title: 'Bathroom Care Specialist',
      verified: true,
    },
    cadence: 'Every Thursday, Afternoon',
    services: ['Bathroom Deep Clean'],
    tags: ['Cleaning', 'Bathroom'],
  },
  {
    id: 'bk-1030',
    reference: '#HOM12704',
    title: 'Full Home Deep Cleaning (2 BHK)',
    category: 'cleaning',
    status: 'completed',
    stage: 'completed',
    when: 'Completed on 4 Jun, 12:14 PM',
    duration: '2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 899,
    payment: { paid: 899, method: 'UPI' },
    professional: {
      name: 'Sunita More',
      rating: 4.8,
      jobs: 396,
      title: 'Cleaning Expert',
      verified: true,
    },
    services: ['Full Home Deep Cleaning (2 BHK)'],
    tags: ['Cleaning', 'Kitchen', '2 BHK'],
  },
  {
    id: 'bk-1024',
    reference: '#HOM12688',
    title: 'Split AC Service',
    category: 'appliance',
    status: 'completed',
    stage: 'completed',
    when: 'Completed on 28 May, 5:40 PM',
    duration: '45 mins',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 299,
    payment: { paid: 299, method: 'UPI' },
    professional: {
      name: 'Ravi Kumar',
      rating: 4.7,
      jobs: 267,
      title: 'Appliance Technician',
      verified: true,
    },
    services: ['Split AC Service'],
    tags: ['Appliance', 'AC'],
  },
  {
    id: 'bk-1019',
    reference: '#HOM12602',
    title: 'Bathroom Deep Clean',
    category: 'cleaning',
    status: 'cancelled',
    stage: 'booked',
    when: '21 May, 9:00 AM',
    duration: '2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 1299,
    payment: { paid: 1299, method: 'HDFC Credit Card •••• 4821' },
    progress: 'You cancelled this booking on 21 May.',
    services: ['Bathroom Deep Clean'],
    tags: ['Cleaning', 'Bathroom'],
  },
  /*
   * Cancelled by us, not by the customer.
   *
   * Seeded because it is the case the detail screen's apology-and-rebook
   * banner exists for, and because it is the one nobody writes test data for:
   * every other cancelled booking in a fixture is one the customer walked away
   * from. `progress` carries the reason, which is what the banner reads.
   */
  {
    id: 'bk-1007',
    reference: '#HOM12488',
    title: 'Full Home Deep Cleaning (2 BHK)',
    category: 'cleaning',
    status: 'cancelled',
    stage: 'booked',
    when: '2 May, 11:00 AM',
    duration: '2 hrs',
    address: '456, Vijay Nagar, Indore, MP 452010',
    price: 899,
    payment: { paid: 899, method: 'UPI • rohan@okhdfcbank' },
    progress:
      'We could not send a professional in your area. Cancelled by Homingo and refunded in full.',
    services: ['Full Home Deep Cleaning (2 BHK)'],
    tags: ['Cleaning', 'Kitchen'],
  },
];

/**
 * A filter is exactly a status - there is no "All".
 *
 * The four statuses answer four different questions, and a combined list
 * answers none of them well: what is happening today does not belong above
 * what was cancelled in May. Dropping "All" also means the list is never
 * mixed, so it needs no group headings to say which run you are looking at.
 */
export type BookingFilter = BookingStatus;

/** The filter chips, in the order they are shown. */
export const bookingFilters: Array<{ key: BookingFilter; label: string }> = [
  { key: 'live', label: 'Live' },
  { key: 'recurring', label: 'Recurring' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

/** What the screen opens on: the work that has not happened yet. */
export const DEFAULT_FILTER: BookingFilter = 'live';

/** Every status, in the order the chips show them. Used by the tests. */
export const statusOrder: BookingStatus[] = [
  'live',
  'recurring',
  'completed',
  'cancelled',
];

export const bookingsFor = (filter: BookingFilter): Booking[] =>
  bookings.filter(booking => booking.status === filter);

/**
 * Every service this customer has actually booked, each named once.
 *
 * Drawn from their own history rather than from the catalogue: the point of
 * the rail is "the things you use", which is a different list from "the things
 * we sell" and a much shorter one. Cancelled bookings count - someone who
 * cancelled a sofa clean still chose it once, and it is a fair thing to offer
 * again.
 *
 * Order is most recent first, because `bookings` is authored that way. When
 * this comes from an API it should be sorted on a real timestamp instead.
 */
export const bookedServiceNames = (): string[] => {
  const seen: string[] = [];
  bookings.forEach(booking => {
    booking.services.forEach(name => {
      if (!seen.includes(name)) {
        seen.push(name);
      }
    });
  });
  return seen;
};

/**
 * The booking a paid order becomes.
 *
 * Built here rather than in the payment screen so that the one place that
 * knows what a `Booking` is stays the one place that makes one. The array
 * above is untouched: this returns a value, and whoever paid holds on to it -
 * a module-level `push` would survive a sign-out and would not survive a
 * reload, which is the worst of both.
 *
 * ---------------------------------------------------------------------------
 * WHAT A REAL ONE WOULD HAVE THAT THIS DOES NOT
 * ---------------------------------------------------------------------------
 * A professional. Nobody is assigned at the moment of payment - dispatch is a
 * separate step - so `professional` is deliberately absent and the detail
 * screen shows "Finding a professional" rather than inventing someone. `eta`,
 * `otp` and `distance` arrive with them.
 */
export const bookingFromOrder = (
  order: {
    reference: string;
    title: string;
    category: CategoryKey;
    when: string;
    /** The scheduled start as an ISO instant, where the cart picked one. */
    slotStartAt?: string;
    mode: BookingMode;
    cadence?: string;
    duration: string;
    address: string;
    services: string[];
    trades: string;
    total: number;
  },
  /** How it was paid, as the receipt reads it - "UPI • rohan@okhdfcbank". */
  paidWith: string,
): Booking => ({
  // The reference is already unique per attempt; deriving the id from it keeps
  // the two impossible to mix up when support has one and the app has the other.
  id: `bk-${order.reference.replace(/^#/, '')}`,
  reference: order.reference,
  title: order.title,
  category: order.category,
  /*
   * `'live'` used to be hardcoded here regardless of what the cart's mode
   * picker said, which is why choosing "Recurring" at checkout produced a
   * booking indistinguishable from an instant one - same status, no cadence,
   * filed under the wrong tab on `BookingsScreen`. `instant` and `schedule`
   * both still resolve to `'live'`: a scheduled visit is booked and will
   * happen, the same as an instant one, just later - the distinction that
   * actually matters to the rest of the app is "does this repeat".
   */
  status: order.mode === 'recurring' ? 'recurring' : 'live',
  // Paid, not yet assigned - the first of the five steps, not the second.
  stage: 'booked',
  when: order.when,
  // Only a scheduled or recurring order has one; instant has no future
  // instant to record. Spread so the field is absent rather than `undefined`.
  ...(order.slotStartAt ? { slotStartAt: order.slotStartAt } : {}),
  duration: order.duration,
  address: order.address,
  price: order.total,
  payment: { paid: order.total, method: paidWith },
  /*
   * `progress` is "only on live" per its own note on `Booking` - a recurring
   * booking's standing fact is its cadence, not a one-off assignment update,
   * so it gets that instead and no `progress` line at all.
   */
  ...(order.mode === 'recurring'
    ? { cadence: order.cadence }
    : { progress: 'Confirmed - assigning a professional' }),
  services: order.services,
  // The trades stand in for hand-authored tags. A seeded booking lists what
  // the visit covers room by room; nothing at checkout knows that, and
  // inventing it would put words in the customer's booking.
  tags: order.trades.split(', ').filter(Boolean),
});
